import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

/**
 * A file an integration has been asked for but has not handed over yet.
 *
 * Uploadcare first copies the picked file from the integration into its own
 * storage, and we then fetch it into the browser. Our uploader has no upload of
 * its own to report until both finish, so this is the only state that can
 * describe that wait to the person who is waiting.
 */
type IntegrationImport = {
  /** Display name of the integration, for example 'Google Drive'. */
  source: string
  /** Uploader the file is bound for. Only that uploader shows the wait. */
  uploaderId: string
}

type IntegrationBarState = {
  /**
   * How many integration imports are running.
   *
   * One bar drives several integrations through a single uploader, so two
   * imports can run at once. A boolean would let the first one to finish
   * report that none are left.
   */
  inFlightCount: number
  activeImport: IntegrationImport | null
}

const initialState: IntegrationBarState = {
  inFlightCount: 0,
  activeImport: null,
}

export const integrationBarSlice = createSlice({
  name: 'integrationBar',
  initialState,
  reducers: {
    integrationUploadStarted: (
      state,
      action: PayloadAction<IntegrationImport | undefined>,
    ) => {
      state.inFlightCount += 1
      // Integrations that hand the file over at once ask for no tile. Such an
      // import must not take down the tile an earlier, slower one is still
      // waiting behind.
      if (action.payload) {
        state.activeImport = action.payload
      }
    },
    integrationUploadFinished: (state) => {
      state.inFlightCount = Math.max(0, state.inFlightCount - 1)
      if (state.inFlightCount === 0) {
        state.activeImport = null
      }
    },
  },
})

export const { integrationUploadStarted, integrationUploadFinished } =
  integrationBarSlice.actions

export type { IntegrationImport }
